/**
 * Time-to-first-token (TTFT) phase-timing contract.
 *
 * Zero-dependency and framework-free on purpose: this module is imported by
 *   - the WDK **workflow bundle** (`apps/web/workflows/chat/*`), which forbids
 *     any Node-reaching import (see the bundle-constraints banner in
 *     `workflow.ts`), and
 *   - the **client** SDK transport (`@bg-bgi/magnus-sdk`), which must stay Node- and
 *     Next-free.
 * Keep it pure — no imports, no side effects.
 *
 * A "phase" is a single wall-clock mark in epoch milliseconds (`Date.now()`).
 * Marks are collected per turn and stamped with the workflow `runId` so the
 * separate server (`ttft.phases`) and client (`ttft.client`) log events can be
 * joined on `runId` in App Insights (KQL) after the fact. The full phase set
 * is debugging telemetry that rides the `@workspace/logger` → App Insights
 * `AppTraces` bridge. Two route-clock deltas are also persisted: the persist
 * step turns `streamFirstModelOutputAt` and `streamFirstTextDeltaAt` minus
 * `routeReceivedAt` into `chat_metric.first_token_ms` / `first_response_ms`
 * (from {@link TTFT_PERSISTED_FROM_MONTH}).
 *
 * Clock caveat: the route replica, the worker replica(s), and the browser are
 * different machines, so *cross-tier* deltas carry clock skew (small and
 * NTP-bounded server-side; unbounded client-side). *Within-machine* deltas —
 * e.g. the `loadChatContext` sub-phases, or `routeUserAppendedAt` minus
 * `routeReceivedAt` — are exact. Prefer same-machine deltas when reading the
 * data; treat cross-tier ones as approximate.
 */

/** A set of named wall-clock marks (epoch ms), collected on one machine. */
export type TtftPhases = Record<string, number>;

/**
 * UTC month (`YYYY-MM`) from which `chat_metric.first_token_ms` /
 * `first_response_ms` carry the route-measured TTFT. Earlier rows are null;
 * dashboards and the devtools caveats quote it when coverage is partial.
 */
export const TTFT_PERSISTED_FROM_MONTH = "2026-10";

/**
 * Record `Date.now()` (or an explicit epoch-ms value) under `name`, unless a
 * value is already present. First-write-wins keeps marks stable under workflow
 * replay and step retries, where the same logical point may execute twice.
 */
export function markPhase(
  phases: TtftPhases,
  name: string,
  at: number = Date.now()
): void {
  if (phases[name] === undefined) {
    phases[name] = at;
  }
}

/**
 * Canonical phase names, shared across the route, the workflow/steps, and the
 * client so the emitted log attributes and the KQL that reads them use one
 * vocabulary. Grouped by the tier that produces the mark.
 */
export const TTFT_PHASE = {
  // ── Client (browser) ──────────────────────────────────────────────
  /** `Date.now()` captured in the composer request body when the user submits. */
  clientSentAt: "clientSentAt",
  /** Just before the SDK transport fires `fetch(POST /api/chat)`. */
  clientSendStart: "clientSendStartAt",
  /** Response headers received (carries `x-workflow-run-id`). */
  clientResponse: "clientResponseAt",
  /** First SSE chunk of any kind decoded by the client. */
  clientFirstChunk: "clientFirstChunkAt",
  /**
   * First model-output chunk decoded by the client (see
   * {@link isModelOutputChunkType}): the moment the UI first shows the model
   * doing something (the "Thinking…" row, a tool card, or text). This is the
   * user-perceived TTFT; the text-delta mark below also counts reasoning and
   * whole tool loops.
   */
  clientFirstModelOutput: "clientFirstModelOutputAt",
  /** First `text-delta` chunk decoded by the client. */
  clientFirstTextDelta: "clientFirstTextDeltaAt",
  /** First animation frame after the first text delta (paint proxy). */
  clientFirstPaint: "clientFirstPaintAt",

  // ── Route (POST /api/chat, real Node time) ────────────────────────
  routeReceived: "routeReceivedAt",
  routeAuthed: "routeAuthedAt",
  routeBodyParsed: "routeBodyParsedAt",
  routeChatEnsured: "routeChatEnsuredAt",
  routeUserAppended: "routeUserAppendedAt",
  // Marks after this point on the route (workflow dispatch, active-stream set)
  // can't reach the workflow — `start(chatWorkflow, …)` only threads the marks
  // captured *before* it via `ChatWorkflowInput.ttftRoute`. Workflow-dispatch
  // cost is instead observable as `ctxStartAt − routeUserAppendedAt`
  // (cross-replica), so there are no `routeWorkflowStartedAt` /
  // `routeActiveStreamSetAt` phases.

  // ── loadChatContext (worker step, real Node time) ─────────────────
  ctxStart: "ctxStartAt",
  /** After the stage-1 `Promise.all` DB fan-out settles. */
  ctxStage1: "ctxStage1DoneAt",
  /** After `materializeMcpCatalogs`. */
  ctxMcp: "ctxMcpDoneAt",
  /** After the enabled/promoted skill lists resolve. */
  ctxSkills: "ctxSkillsDoneAt",
  /** Routed Auto turns only: just before the routing decision is awaited. */
  ctxRouteWaitStart: "ctxRouteWaitStartAt",
  /** Routed Auto turns only: routing decision in hand. Minus
   *  `ctxRouteWaitStartAt` = the TTFT the router actually added. */
  ctxRouted: "ctxRoutedAt",
  /** After system-prompt build + document hydrate/render + convert + prefetch. */
  ctxPromptBuilt: "ctxPromptBuiltAt",
  /** loadChatContext returns. */
  ctxReady: "ctxReadyAt",

  // ── Turn start (worker, real Node time) ───────────────────────────
  /** Before `emitTurnStart` writes the message envelope to the belt. */
  emitTurnStart: "emitTurnStartAt",
  /** After `emitTurnStart` finishes writing. */
  emitTurnStartDone: "emitTurnStartDoneAt",
  /** Durable run start (`getRun().startedAt`), the worker-side anchor. */
  runStarted: "runStartedAt",
  /**
   * Gateway clock: when AI Gateway called the upstream provider that served
   * the turn's first model step (`routing.modelAttempts[].providerAttempts[]
   * .startTime`). `modelUpstreamStartAt − emitTurnStartDoneAt` is the
   * `doStreamStep` dispatch plus gateway pre-upstream time;
   * `streamFirstModelOutputAt − modelUpstreamStartAt` is provider time to
   * first output plus the belt. Cross-machine (Vercel vs. Azure), so NTP
   * skew applies. Absent on the direct-provider path.
   */
  modelUpstreamStart: "modelUpstreamStartAt",

  // ── Stream serve (route replica, real Node time) ──────────────────
  // Stamped by the belt→SSE tap on the route while serving the live POST
  // stream, and emitted as the separate `ttft.stream` event (the workflow
  // never sees the live chunks). The tap also hands `streamFirstModelOutputAt`
  // and `streamFirstTextDeltaAt` to the persist step through Redis
  // (`@workspace/redis/ttft-marks`), so when they arrive in time they ride
  // `ttft.phases` too. Route↔worker deltas are NTP-bounded (~ms), so e.g.
  // `streamFirstTextDeltaAt − emitTurnStartDoneAt` is a sound model-TTFT
  // proxy. There is deliberately no provider-clock mark: the SDK's
  // `steps[*].response.timestamp` is a provider/gateway-reported metadata
  // date with no first-byte semantics and was observed landing tens of
  // seconds late — do not resurrect it.
  /** First chunk of any kind leaving the route for this run's live stream. */
  streamFirstChunk: "streamFirstChunkAt",
  /** First model-produced chunk (text/reasoning/tool output) leaving the route. */
  streamFirstModelOutput: "streamFirstModelOutputAt",
  /** First `text-delta` chunk leaving the route — server-side TTFT. */
  streamFirstTextDelta: "streamFirstTextDeltaAt",
} as const;

/**
 * UI message chunk types that are envelope/control rather than model output.
 * Everything else (text/reasoning starts and deltas, tool input, files,
 * sources) means the model has produced something the UI renders.
 */
const NON_MODEL_OUTPUT_CHUNK_TYPES = new Set([
  "start",
  "start-step",
  "finish-step",
  "finish",
  "message-metadata",
  "abort",
  "error",
]);

/** Whether a UI message chunk `type` is model output (`data-*` parts are app-emitted, not model output). */
export function isModelOutputChunkType(type: string): boolean {
  return !NON_MODEL_OUTPUT_CHUNK_TYPES.has(type) && !type.startsWith("data-");
}

/**
 * Cap on the number of phase marks accepted from an untrusted client payload,
 * so a malformed/hostile beacon can't bloat a single log record. The client
 * contract has ~6 marks; the ceiling leaves generous headroom.
 */
export const MAX_TTFT_PHASES = 24;

/**
 * Defensively coerce an untrusted value (e.g. a `sendBeacon` body field) into a
 * `TtftPhases` map: object-only, finite-number values only, capped at
 * `MAX_TTFT_PHASES` entries. Never throws — returns `{}` for anything that
 * isn't a plain object.
 */
export function coercePhases(value: unknown): TtftPhases {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }
  const out: TtftPhases = {};
  let count = 0;
  for (const [key, raw] of Object.entries(value as Record<string, unknown>)) {
    if (count >= MAX_TTFT_PHASES) {
      break;
    }
    if (typeof raw === "number" && Number.isFinite(raw)) {
      out[key] = raw;
      count++;
    }
  }
  return out;
}

/** The client marks a browser reports for one turn, keyed by workflow run. */
export interface ClientTtftRecord {
  chatId: string;
  phases: TtftPhases;
  /** `x-workflow-run-id` from the send response; `null` if the header was absent. */
  runId: string | null;
}

/**
 * Flatten a phase map plus turn identifiers into scalar log attributes for
 * `@workspace/logger` (which forwards string/number/boolean attributes to the
 * App Insights `AppTraces` bridge). Non-finite phase values are dropped.
 */
export function buildTtftLogAttributes(input: {
  runId: string;
  chatId?: string;
  model?: string;
  appSurface?: string;
  isNewChat?: boolean;
  origin: "server" | "client" | "stream";
  phases: TtftPhases;
}): Record<string, string | number | boolean> {
  const attrs: Record<string, string | number | boolean> = {
    event: `ttft.${input.origin}`,
    runId: input.runId,
  };
  if (input.chatId) {
    attrs.chatId = input.chatId;
  }
  if (input.model) {
    attrs.model = input.model;
  }
  if (input.appSurface) {
    attrs.appSurface = input.appSurface;
  }
  if (typeof input.isNewChat === "boolean") {
    attrs.isNewChat = input.isNewChat;
  }
  for (const [key, value] of Object.entries(input.phases)) {
    if (typeof value === "number" && Number.isFinite(value)) {
      attrs[key] = value;
    }
  }
  return attrs;
}
